import * as Sentry from '@sentry/browser';
import type { ErrorBoundaryPropsWithRender, FallbackProps } from 'react-error-boundary';
import { ErrorBoundary as ErrBoundary } from 'react-error-boundary';
import { isNil } from 'lodash-es';

import { useTranslation } from '@hotjar/translations';
import { useNotify } from '@hotjar/hotsauce';
import { logError } from '@hotjar/insights-common/lib/logger';

export type SquadSentryTag =
  | 'acquisition'
  | 'activation'
  | 'billing'
  | 'business-enablement'
  | 'core-ux'
  | 'design-system'
  | 'developer-experience'
  | 'feedback'
  | 'heatmaps'
  | 'hotjar' // <- equivalent to 'shared' in term of ownership
  | 'integrations'
  | 'monetization-platform'
  | 'product-foundations'
  | 'recordings'
  | 'revenue'
  | 'security-and-auth'
  | 'self-service'
  | 'supply-chain'
  | 'upsell'
  | 'engage'
  | 'tame'
  | 'workflow';

export type ErrorCriticality = 'unknown' | 'minor' | 'major' | 'critical';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  squadSentryTag?: SquadSentryTag;
  fallbackComponent?: React.ComponentType<FallbackProps>;
  fallbackRender?: ErrorBoundaryPropsWithRender['fallbackRender'];
  onError?: (error: Error) => unknown;
  onReset?: (...args: Array<unknown>) => void;
  resetKeys?: Array<unknown>;
  featureName?: string;
  criticality?: ErrorCriticality;
  shouldShowToast?: boolean;
  toastMessage?: string;
}

interface CaptureExceptionProps {
  error: Error;
  squadSentryTag?: SquadSentryTag;
  featureName?: string;
  criticality?: ErrorCriticality;
  extra?: Record<string, unknown>;
}

const captureException = ({
  error,
  squadSentryTag,
  featureName,
  criticality,
  extra,
}: CaptureExceptionProps): void => {
  Sentry.withScope((scope) => {
    const tags = {
      ...(squadSentryTag && { squad: squadSentryTag }),
      ...(criticality && { criticality }),
      ...(!isNil(featureName) && { feature: featureName }),
    };

    scope.setTags(tags);

    if (extra) {
      scope.setExtras(extra);
    }

    logError(error);

    // We don't use the tracking package yet because it depends on webapp.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { hj } = window as any;
    if (!isNil(hj)) {
      hj('tagRecording', ['ErrorBoundary triggered']);
    }
  });
};

const DefaultFallbackComponent: React.ComponentType<FallbackProps> = () => {
  return <div />;
};

export const ErrorBoundary = ({
  children,
  squadSentryTag,
  fallbackComponent = DefaultFallbackComponent,
  fallbackRender,
  onReset,
  resetKeys,
  onError,
  featureName,
  criticality = 'unknown',
  shouldShowToast = true,
  toastMessage,
}: ErrorBoundaryProps): JSX.Element => {
  const { showToast } = useNotify();
  const { t } = useTranslation();

  const handleError = (error: Error, info: { componentStack: string }) => {
    if (shouldShowToast) {
      showToast({
        text: toastMessage ?? t('common:error.default-message'),
        kind: 'danger',
      });
    }
    captureException({ error, squadSentryTag, featureName, criticality, extra: info });

    if (typeof onError === 'function') {
      onError(error);
    }
  };

  return (
    <ErrBoundary
      FallbackComponent={fallbackComponent}
      fallbackRender={fallbackRender}
      onError={handleError}
      onReset={onReset}
      resetKeys={resetKeys}
    >
      {children}
    </ErrBoundary>
  );
};

// We also export the hook as is to avoid having to declare react-error-boundary as a dep in other packages
export { useErrorHandler } from 'react-error-boundary';
